import * as React from "react";
import { useState, useEffect } from "react";
import {
    Alert, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Link, Typography
} from "@mui/material";

type CheckerState = "idle" | "running" | "approved" | "pendingReview" | "failed" | "unauthorized" | "error";

interface UnapprovedHardwareNotificationProps {
    isVisible: boolean;
    deviceSerialNumber?: string;
    authToken?: string;
    apiUrl?: string;
    onDismiss?: () => void;
}

export default function UnapprovedHardwareNotification(props: UnapprovedHardwareNotificationProps) {
    const { isVisible, deviceSerialNumber, authToken, apiUrl, onDismiss } = props;
    const [checkerState, setCheckerState] = useState<CheckerState>("idle");
    const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!isVisible) {
            setCheckerState("idle");
            setErrorDetail(undefined);
        }
    }, [isVisible]);

    const hardwareCheckerApi = window.hardwareCheckerApi;

    const runChecker = async () => {
        if (!hardwareCheckerApi || !authToken || !apiUrl) return;
        setCheckerState("running");
        setErrorDetail(undefined);
        try {
            const result = await hardwareCheckerApi.run(authToken, apiUrl);
            switch (result.exitCode) {
                case 0: setCheckerState("approved"); break;
                case 1: setCheckerState("pendingReview"); break;
                case 2: setCheckerState("failed"); break;
                case 3: setCheckerState("unauthorized"); break;
                default:
                    setCheckerState("error");
                    setErrorDetail(result.error ?? `Exit code: ${result.exitCode}`);
            }
        } catch (e) {
            setCheckerState("error");
            setErrorDetail(String(e));
        }
    };

    const canDismiss = checkerState !== "running" && checkerState !== "approved";

    const renderContent = () => {
        switch (checkerState) {
            case "idle":
                return (
                    <>
                        <Typography variant="body2">
                            Your account has HDC V3 devices, but this computer is not yet approved
                            for automatic data collection.
                        </Typography>
                        {deviceSerialNumber && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Your Hardware ID:</strong> {deviceSerialNumber}
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Run the hardware compatibility checker to request approval for this computer.
                        </Typography>
                    </>
                );
            case "running":
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Running hardware compatibility check...</Typography>
                    </div>
                );
            case "approved":
                return (
                    <Alert severity="success">
                        This computer has been approved. Please restart iDART to enable automatic data collection.
                    </Alert>
                );
            case "pendingReview":
                return (
                    <Alert severity="info">
                        Your approval request has been submitted and is pending review by Inductosense.
                        You will be notified once it has been approved.
                    </Alert>
                );
            case "failed":
                return (
                    <Alert severity="error">
                        The hardware compatibility check failed. Please{" "}
                        <Link href="mailto:support@inductosense.com">contact Inductosense support</Link>{" "}
                        for assistance.
                    </Alert>
                );
            case "unauthorized":
                return (
                    <Alert severity="warning">
                        This computer is not authorised for this account. Please{" "}
                        <Link href="mailto:support@inductosense.com">contact Inductosense support</Link>{" "}
                        if you believe this is an error.
                    </Alert>
                );
            case "error":
                return (
                    <Alert severity="error">
                        An unexpected error occurred while running the hardware checker
                        {errorDetail ? `: ${errorDetail}` : "."}{" "}
                        Please{" "}
                        <Link href="mailto:support@inductosense.com">contact Inductosense support</Link>{" "}
                        if the problem persists.
                    </Alert>
                );
        }
    };

    return (
        <Dialog
            open={isVisible}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={!canDismiss}
            onClose={canDismiss ? onDismiss : undefined}
        >
            <DialogTitle>HDC V3 Hardware Approval Required</DialogTitle>
            <DialogContent>
                {renderContent()}
            </DialogContent>
            <DialogActions>
                {checkerState === "approved" && (
                    <Button variant="contained" color="primary" onClick={() => hardwareCheckerApi?.restart()}>
                        Restart iDART
                    </Button>
                )}
                {checkerState === "idle" && hardwareCheckerApi && (
                    <Button variant="contained" onClick={runChecker} disabled={!authToken || !apiUrl}>
                        Check Device Compatibility
                    </Button>
                )}
                {(checkerState === "error" || checkerState === "failed") && (
                    <Button variant="outlined" onClick={runChecker}>
                        Retry
                    </Button>
                )}
                {canDismiss && onDismiss && (
                    <Button onClick={onDismiss} variant="text">
                        {checkerState === "idle" ? "Ask Me Later" : "Close"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
